import { KommunList } from "types/Kommun.type";
import Dropdown from "./Dropdown";
import { UseFormRegister } from "react-hook-form";
import { useGuessKommun } from "api/postnummer.api";
import { useEffect, useRef } from "react";
import { removeWhitespace } from "helpers/string";
import { isValidPostnummer } from "types/transport.type";

type Props = {
  isSetAutomaticallyByPostalCodeEnabled?: boolean;
  postnummer?: string | null;
  name: string;
  register: UseFormRegister<any>;
  setKommunKod: (k: string) => void;
  setPostalCity?: (city: string) => void;
  errorMessage?: string;
};
export const KommunkodDropdown = ({
  isSetAutomaticallyByPostalCodeEnabled = true,
  postnummer,
  name,
  register,
  setKommunKod,
  setPostalCity,
  errorMessage,
}: Props) => {
  // The postal code most recently sent to the lookup, seeded with the value
  // the component mounted with. Only a *changed* postal code triggers a
  // lookup: the edit modal does not look up its saved postal code on open,
  // re-renders with the same value do not refire, and going back to an
  // earlier value does.
  const trimmedPostnummer = removeWhitespace(postnummer ?? "");
  const lastLookedUpPostnummer = useRef<string>(trimmedPostnummer);
  // Current value, readable from the result effect without re-running it on
  // every keystroke (a result must be applied at most once).
  const currentPostnummer = useRef(trimmedPostnummer);
  currentPostnummer.current = trimmedPostnummer;
  const { mutate, data, isPending } = useGuessKommun();

  useEffect(() => {
    if (
      isSetAutomaticallyByPostalCodeEnabled &&
      isValidPostnummer(trimmedPostnummer) &&
      trimmedPostnummer !== lastLookedUpPostnummer.current
    ) {
      lastLookedUpPostnummer.current = trimmedPostnummer;
      mutate({
        postnummer: trimmedPostnummer,
      });
    }
  }, [trimmedPostnummer, isSetAutomaticallyByPostalCodeEnabled, mutate]);

  // Whether the kommunkod currently in the form came from a lookup. An
  // unknown postal code only clears a kommunkod the lookup itself set, so a
  // saved or manually chosen kommunkod survives an empty lookup.
  const hasSetKommunKodFromLookup = useRef(false);

  // The modals pass inline arrows, so the callbacks get a new identity on
  // every render. Read them through refs so the result effect depends only
  // on `data` and applies each result exactly once.
  const setKommunKodRef = useRef(setKommunKod);
  setKommunKodRef.current = setKommunKod;
  const setPostalCityRef = useRef(setPostalCity);
  setPostalCityRef.current = setPostalCity;

  useEffect(() => {
    if (!data) {
      return;
    }
    // A result that lands after the postal code was changed again (e.g. the
    // user deleted a digit) no longer belongs to the field. Drop it and
    // forget the lookup, so that retyping the same code looks it up anew.
    if (currentPostnummer.current !== lastLookedUpPostnummer.current) {
      lastLookedUpPostnummer.current = "";
      return;
    }
    if (data.kommunKod.length === 4) {
      hasSetKommunKodFromLookup.current = true;
      setKommunKodRef.current(data.kommunKod);
    } else if (hasSetKommunKodFromLookup.current) {
      hasSetKommunKodFromLookup.current = false;
      setKommunKodRef.current("");
    }
    setPostalCityRef.current?.(data.postalCity);
  }, [data]);

  return (
    <Dropdown
      items={KommunList.map((kommun) => ({
        label: kommun.label,
        value: kommun.code,
      }))}
      label="Kommun"
      name={name}
      register={register}
      //   action={
      //     !postnummer
      //       ? undefined
      //       : () =>
      //           guessKommunMutation.mutate({
      //             postnummer: removeWhitespace(postnummer),
      //           })
      //   }
      actionText="Beräkna från postnummer"
      isActionLoading={isPending}
      descriptionItemText=" "
      disabledOptionValue={""}
      defaultValue=""
      errorMessage={errorMessage}
    />
  );
};
